// apps/roleforge-app/src/lib/course-content.ts
//
// RoleForge v2 Plan 2, Task 3 — the DIY Implementation Course library.
//
// Fixed content as data (no CMS). `videoUrl: null` renders a calm
// "coming soon — written steps below" state so the page ships before any
// video is recorded; the owner drops real URLs in later (one-line edits).
//
// The written steps mirror the bundle README's import map (renderReadmeV2 in
// apps/server/src/services/roleforge-export-bundle-v2.ts) — the course and
// the README must not disagree about how a seat is imported.

export interface CourseModule {
  id: string;
  title: string;
  description: string;
  /**
   * Video for the module, or null while unrecorded (renders a calm
   * "coming soon" state). Accepted shapes (normalized by CoursePage's
   * toEmbedUrl before iframing):
   *   - YouTube: watch (`youtube.com/watch?v=<id>`), share (`youtu.be/<id>`),
   *     or embed (`youtube.com/embed/<id>`) URLs
   *   - Vimeo: `vimeo.com/<id>` or `player.vimeo.com/video/<id>`
   *   - Anything else (e.g. a direct .mp4 URL) plays in a plain <video> tag
   */
  videoUrl: string | null;
  /** Written instructions shown under the video (or in its place while videoUrl is null). */
  steps: string[];
}

export const COURSE_MODULES: CourseModule[] = [
  {
    id: 'orientation',
    title: 'Your Blueprint Bundle, explained',
    description: 'What each file is and how the pieces fit.',
    videoUrl: null,
    steps: [
      'Download your Blueprint Bundle from the delivery page and unzip it.',
      'Open `report.md` — the full Blueprint: your business in your words, the department map, every seat, the build order. (`report.pdf` is the same report rendered as a PDF.)',
      'Open `departments.md` for the department map, including need-to-know compartments and why each exists.',
      'Each seat lives in `seats/<seat>/`: `charter.md` (what the human does, what the AI does, access, decision rights), `agent-instructions.md` (drop-in role instructions), and `skills/<skill>/SKILL.md` (portable skill folders).',
      'Follow the build order in the report — set up one seat at a time, starting with the first.',
    ],
  },
  {
    id: 'claude-code',
    title: 'Import into Claude Code',
    description: 'Skills folders + agent instructions.',
    videoUrl: null,
    steps: [
      'Copy each `seats/<seat>/skills/<skill>` folder into `.claude/skills/` in your project.',
      "Paste the seat's `agent-instructions.md` into your project's `CLAUDE.md` (or reference it from there).",
      'Start a session and ask it to work the seat.',
    ],
  },
  {
    id: 'cowork',
    title: 'Import into CoWork',
    description: 'Load your seat skills and instructions into CoWork.',
    videoUrl: null,
    steps: [
      'Add each `seats/<seat>/skills/<skill>` folder as a skill in CoWork.',
      "Use the seat's `agent-instructions.md` as the working instructions for that seat.",
    ],
  },
  {
    id: 'codex',
    title: 'Import into Codex',
    description: 'Set up seats and skills in Codex.',
    videoUrl: null,
    steps: [
      "Paste the seat's `agent-instructions.md` into your repo's `AGENTS.md`.",
      'Copy the `SKILL.md` contents into the instructions for the matching task, or keep the skill folders alongside the repo.',
    ],
  },
  {
    id: 'chatgpt-work',
    title: 'Use with ChatGPT Work',
    description: 'Turn seat instructions into custom GPT setups.',
    videoUrl: null,
    steps: [
      "Open custom instructions and paste the seat's `agent-instructions.md`.",
      'Paste the relevant `SKILL.md` body into the conversation when you want that capability.',
    ],
  },
  {
    id: 'operating',
    title: 'Running your workforce week to week',
    description: 'Working rhythm: what to delegate, review, and adjust.',
    videoUrl: null,
    steps: [
      "Start each week by picking the work each seat should carry — the seat charter's responsibilities are the menu.",
      "Delegate whole outcomes, not keystrokes: hand a seat the task plus the context it needs, and let its instructions do the shaping.",
      "Review what comes back against the seat's decision rights in `charter.md` — approve what the charter says the AI decides, and keep the human calls human.",
      'When a seat keeps getting something wrong, adjust its `agent-instructions.md` or the relevant `SKILL.md` — small edits compound.',
      "Revisit the department map monthly: as work shifts, seats move too. Your Blueprint is a living document, not a one-time setup.",
    ],
  },
];
